import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useClearRefinements, useRange, useRefinementList } from 'react-instantsearch';
import { Button, Modal, RangeSlider } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import css from './StoreProfileDesktopFiltersModal.module.css';

const PRICE_STEP = 1;
const LOGICAL_DEFAULT_PRICE_RANGE = [0, 10000];
const FILTER_SECTIONS = [
  {
    attribute: 'publicData.category',
    title: 'AlgoliaFilters.section.category',
  },
  {
    attribute: 'publicData.access_to_rentals',
    title: 'AlgoliaFilters.section.accessToRentals',
  },
  {
    attribute: 'publicData.Condition_of_the_item',
    title: 'AlgoliaFilters.section.conditionOfTheItem',
  },
  {
    attribute: 'publicData.Trust_and_verification',
    title: 'AlgoliaFilters.section.trustAndVerification',
  },
  {
    attribute: 'publicData.cancelation_policy',
    title: 'AlgoliaFilters.section.cancellationPolicy',
  },
  {
    attribute: 'publicData.reason_for_listing',
    title: 'AlgoliaFilters.section.reasonForListing',
  },
  {
    attribute: 'publicData.security_deposit',
    title: 'AlgoliaFilters.section.securityDeposit',
  },
];
const FILTER_ATTRIBUTES = FILTER_SECTIONS.map(section => section.attribute);
const CLEARABLE_ATTRIBUTES = [...FILTER_ATTRIBUTES, 'price.amount'];
const ATTRIBUTE_TO_QUERY_KEY = {
  'publicData.category': 'category',
  'publicData.access_to_rentals': 'access_to_rentals',
  'publicData.Condition_of_the_item': 'Condition_of_the_item',
  'publicData.Trust_and_verification': 'Trust_and_verification',
  'publicData.cancelation_policy': 'cancelation_policy',
  'publicData.reason_for_listing': 'reason_for_listing',
  'publicData.security_deposit': 'security_deposit',
};

const toDollars = amount => Math.round((Number(amount) || 0) / 100);
const toCents = amount => Math.round((Number(amount) || 0) * 100);
const clampPriceRange = (rangeValue, availableRange) => {
  const [availableMin, availableMax] = availableRange;
  const rawMin = Number(rangeValue?.[0]);
  const rawMax = Number(rangeValue?.[1]);
  const min = Number.isFinite(rawMin) ? rawMin : availableMin;
  const max = Number.isFinite(rawMax) ? rawMax : availableMax;

  const validPriceRange = [
    Math.max(availableMin, Math.min(min, availableMax)),
    Math.max(availableMin, Math.min(max, availableMax)),
  ];
  if (validPriceRange[0] > validPriceRange[1]) {
    validPriceRange[0] = validPriceRange[1];
  }
  return [Math.round(validPriceRange[0]), Math.round(validPriceRange[1])];
};

const useFilterSections = () => {
  const category = useRefinementList({
    attribute: 'publicData.category',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const access = useRefinementList({
    attribute: 'publicData.access_to_rentals',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const condition = useRefinementList({
    attribute: 'publicData.Condition_of_the_item',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const trust = useRefinementList({
    attribute: 'publicData.Trust_and_verification',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const cancelation = useRefinementList({
    attribute: 'publicData.cancelation_policy',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const reason = useRefinementList({
    attribute: 'publicData.reason_for_listing',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });
  const deposit = useRefinementList({
    attribute: 'publicData.security_deposit',
    limit: 100,
    sortBy: ['isRefined:desc', 'name:asc'],
  });

  return useMemo(
    () => [
      { ...FILTER_SECTIONS[0], ...category },
      { ...FILTER_SECTIONS[1], ...access },
      { ...FILTER_SECTIONS[2], ...condition },
      { ...FILTER_SECTIONS[3], ...trust },
      { ...FILTER_SECTIONS[4], ...cancelation },
      { ...FILTER_SECTIONS[5], ...reason },
      { ...FILTER_SECTIONS[6], ...deposit },
    ],
    [category, access, condition, trust, cancelation, reason, deposit]
  );
};

const FilterSection = ({ title, items, selectedValue, onSelect }) => {
  if (!items?.length) return null;

  return (
    <div className={css.section}>
      <h3 className={css.sectionTitle}>
        <FormattedMessage id={title} defaultMessage={title} />
      </h3>
      <div className={css.options}>
        {items.map(item => (
          <button
            key={item.value}
            type="button"
            className={`${css.option} ${selectedValue === item.value ? css.optionSelected : ''}`}
            onClick={() =>
              onSelect(selectedValue === item.value ? null : item.value)
            }
          >
            <FormattedMessage
              id={`enum.${item.value}.label`}
              defaultMessage={item.label}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

FilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const StoreProfileDesktopFiltersModal = ({
  isOpen,
  onClose,
  onManageDisableScrolling,
  initialListingFilters,
  onApplyFilters,
}) => {
  const sectionHooks = useFilterSections();
  const hasHydratedFromUrlRef = useRef(false);
  const hasInitializedOpenStateRef = useRef(false);

  const {
    range,
    start,
    canRefine: canRefinePrice,
    refine: refinePriceRange,
  } = useRange({
    attribute: 'price.amount',
  });
  const { refine: clearSelectedRefinements } = useClearRefinements({
    includedAttributes: CLEARABLE_ATTRIBUTES,
  });

  const [draftSelections, setDraftSelections] = useState({});
  const [draftPriceRange, setDraftPriceRange] = useState(LOGICAL_DEFAULT_PRICE_RANGE);

  const availablePriceRange = useMemo(() => {
    const min = Number.isFinite(range?.min) ? toDollars(range.min) : 0;
    const max = Number.isFinite(range?.max) ? toDollars(range.max) : 10000;
    return [min, max];
  }, [range?.min, range?.max]);

  const effectiveDefaultPriceRange = useMemo(() => {
    return clampPriceRange(LOGICAL_DEFAULT_PRICE_RANGE, availablePriceRange);
  }, [availablePriceRange]);

  const defaultPriceRange = useMemo(() => {
    const encodedRange = initialListingFilters?.priceRange;
    if (typeof encodedRange === 'string' && encodedRange.includes(',')) {
      const [min, max] = encodedRange.split(',');
      const decodedRange = [toDollars(min), toDollars(max)];
      return clampPriceRange(decodedRange, availablePriceRange);
    }
    return effectiveDefaultPriceRange;
  }, [initialListingFilters, availablePriceRange, effectiveDefaultPriceRange]);

  useEffect(() => {
    if (hasHydratedFromUrlRef.current) return;
    if (!initialListingFilters) {
      hasHydratedFromUrlRef.current = true;
      return;
    }

    sectionHooks.forEach(section => {
      const queryKey = ATTRIBUTE_TO_QUERY_KEY[section.attribute];
      const value = initialListingFilters[queryKey];
      if (!value) return;

      const alreadyRefined = (section.items || []).some(
        item => item.value === value && item.isRefined
      );
      if (!alreadyRefined) {
        section.refine(value);
      }
    });

    const priceRange = initialListingFilters.priceRange;
    if (priceRange && typeof priceRange === 'string' && priceRange.includes(',')) {
      const [min, max] = priceRange.split(',');
      const minCents = Number(min);
      const maxCents = Number(max);
      if (Number.isFinite(minCents) && Number.isFinite(maxCents)) {
        refinePriceRange([minCents, maxCents]);
      }
    }

    hasHydratedFromUrlRef.current = true;
  }, [initialListingFilters, refinePriceRange, sectionHooks]);

  useEffect(() => {
    if (!isOpen) {
      hasInitializedOpenStateRef.current = false;
      return;
    }
    if (hasInitializedOpenStateRef.current) return;
    hasInitializedOpenStateRef.current = true;

    const nextSelections = {};
    sectionHooks.forEach(section => {
      const selected = (section.items || []).find(item => item.isRefined);
      if (selected?.value) {
        nextSelections[section.attribute] = selected.value;
      }
    });
    setDraftSelections(nextSelections);

    const [currentMin, currentMax] = start || [];
    const resolvedMin = Number.isFinite(currentMin)
      ? toDollars(currentMin)
      : defaultPriceRange[0];
    const resolvedMax = Number.isFinite(currentMax)
      ? toDollars(currentMax)
      : defaultPriceRange[1];
    setDraftPriceRange(clampPriceRange([resolvedMin, resolvedMax], availablePriceRange));
  }, [isOpen, start, availablePriceRange, defaultPriceRange, sectionHooks]);

  const hasDraftRefinements = useMemo(() => {
    const hasAnySelection = Object.values(draftSelections).some(Boolean);
    if (hasAnySelection) return true;

    return (
      draftPriceRange[0] !== defaultPriceRange[0] ||
      draftPriceRange[1] !== defaultPriceRange[1]
    );
  }, [draftSelections, draftPriceRange, defaultPriceRange]);

  const handlePriceRangeChange = useCallback(
    value => {
      setDraftPriceRange(clampPriceRange(value, availablePriceRange));
    },
    [availablePriceRange]
  );

  const handleApply = () => {
    const appliedQueryFilters = {};
    // Start from a clean slate so "clear all" + apply always removes old refinements.
    clearSelectedRefinements();

    sectionHooks.forEach(section => {
      const selectedValue = draftSelections[section.attribute] || null;
      if (selectedValue) {
        section.refine(selectedValue);
      }

      const queryKey = ATTRIBUTE_TO_QUERY_KEY[section.attribute];
      if (queryKey && selectedValue) {
        appliedQueryFilters[queryKey] = selectedValue;
      }
    });

    const hasCustomPrice =
      draftPriceRange[0] !== effectiveDefaultPriceRange[0] ||
      draftPriceRange[1] !== effectiveDefaultPriceRange[1];

    if (hasCustomPrice) {
      const minCents = toCents(draftPriceRange[0]);
      const maxCents = toCents(draftPriceRange[1]);
      refinePriceRange([minCents, maxCents]);
      appliedQueryFilters.priceRange = `${minCents},${maxCents}`;
    } else {
      refinePriceRange([undefined, undefined]);
    }

    if (onApplyFilters) {
      onApplyFilters(appliedQueryFilters);
    }

    onClose();
  };

  const handleClearAll = () => {
    clearSelectedRefinements();
    setDraftSelections({});
    setDraftPriceRange(effectiveDefaultPriceRange);
    if (onApplyFilters) {
      onApplyFilters({});
    }
  };

  return (
    <Modal
      id="StoreProfileDesktopFilters"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      className={css.modalRoot}
      usePortal
    >
      <div className={css.modalContent}>
        <div className={css.modalHeader}>
          <h2 className={css.modalTitle}>
            <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
          </h2>
        </div>

        <div className={css.modalBody}>
          {sectionHooks.map(section => (
            <FilterSection
              key={section.attribute}
              title={section.title}
              items={section.items}
              selectedValue={draftSelections[section.attribute]}
              onSelect={value =>
                setDraftSelections(prev => ({ ...prev, [section.attribute]: value }))
              }
            />
          ))}

          {canRefinePrice ? (
            <div className={css.section}>
              <h3 className={css.sectionTitle}>
                <FormattedMessage
                  id="AlgoliaFilters.section.priceRange"
                  defaultMessage="Price Range"
                />
              </h3>
              <div className={css.priceRange}>
                <RangeSlider
                  min={availablePriceRange[0]}
                  max={availablePriceRange[1]}
                  step={PRICE_STEP}
                  handles={draftPriceRange}
                  onChange={handlePriceRangeChange}
                />
                <div className={css.priceLabels}>
                  <span>
                    <FormattedMessage id="AlgoliaFilters.section.priceMin" />
                  </span>
                  <span>
                    <FormattedMessage id="AlgoliaFilters.section.priceMax" />
                  </span>
                </div>
                <div className={css.priceLabels}>
                  <span className={css.inputBox}>${draftPriceRange[0]}</span>
                  <span className={css.inputBox}>${draftPriceRange[1]}</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className={css.footer}>
          <Button
            type="button"
            className={css.clearBtn}
            onClick={handleClearAll}
            disabled={!hasDraftRefinements}
            variant='secondary-outline'
          >
            <FormattedMessage id="AlgoliaFilters.clearAll" defaultMessage="Clear all" />
          </Button>
          <Button type="button" variant='primary' className={css.applyBtn} onClick={handleApply}>
            <FormattedMessage
              id="AlgoliaFilters.showRentals"
              defaultMessage="Show Rentals"
            />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

StoreProfileDesktopFiltersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func,
  initialListingFilters: PropTypes.object,
  onApplyFilters: PropTypes.func,
};

export default StoreProfileDesktopFiltersModal;
